import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joi from 'joi';

import { useGetSGPsQuery, useUpdateInvestmentFundMutation, useGetPromotersQuery } from 'api/main';
import { checkImageFile, getFileExtension, validateFileSize, validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    CustomFormLabel,
    CustomTextField,
    ExpandableList,
    FileErrorMessage,
    FileInputButton,
    SuccessMessage,
    ActionButton,
    CustomSelect,
    Loader,
    ConfirmDialog,
} from 'components';
import { Box, Button, Link, MenuItem, Typography } from '@mui/material';

const containerWidth = { lg: '50%', md: '100%', xl: '35%' };

const renderSGP = sgp => (
    <MenuItem key={sgp.id} value={sgp.id}>
        {sgp.name}
    </MenuItem>
);

const FileName = ({ file, name }) =>
    file?.url ? (
        <Link
            variant="body2"
            sx={{ flex: '0 1 65%' }}
            textOverflow="ellipsis"
            noWrap
            title={file?.name}
            gutterBottom
            target="_blank"
            href={file?.url}
        >
            {name}
        </Link>
    ) : (
        <Typography
            variant="body2"
            sx={{ flex: '0 1 65%' }}
            textOverflow="ellipsis"
            noWrap
            title={name}
            gutterBottom
        >
            {name}
        </Typography>
    );

const FundFile = props => {
    const { onChange, onDelete, id, name, sizeError, typeError, label, file, ...rest } = props;
    const [deleteFileOpen, setDeleteFileOpen] = useState(false);
    return (
        <Box
            sx={{ width: containerWidth }}
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="flex-start"
        >
            <CustomFormLabel>{label}</CustomFormLabel>
            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
                {(file?.url || file?.name) && <FileName file={file} name={name} />}
                <Box sx={{ textAlign: 'right' }}>
                    <FileInputButton
                        id={id}
                        {...rest}
                        noMargin
                        showIcon
                        onFileChange={onChange}
                        label={texts.UPLOAD_FILE_BUTTON}
                    />
                    {file?.url && onDelete && (
                        <Button onClick={() => setDeleteFileOpen(true)}>{texts.SUPPRIMER}</Button>
                    )}
                    {file?.url && onDelete && (
                        <ConfirmDialog
                            open={deleteFileOpen}
                            title={texts.SUPPRIMER}
                            handleClose={() => setDeleteFileOpen(false)}
                            handleConfirm={onDelete}
                        >
                            {texts.ETES_VOUS_SURS_DE_VOULOIR_SUPPRIMER}
                        </ConfirmDialog>
                    )}
                </Box>
            </Box>
            {typeError && <FileErrorMessage useMargin={false} variant="type" />}
            {sizeError && <FileErrorMessage useMargin={false} variant="size" />}
        </Box>
    );
};

const FundAttributes = props => {
    const { fund, refetch } = props;
    const [updateInvestmentFund, { isLoading, isSuccess }] = useUpdateInvestmentFundMutation();
    const { data: { sgps = [] } = { sgps: [], lastPage: 1 }, isLoading: sgpsLoading } =
        useGetSGPsQuery({});
    const { data: { promoters = [] } = { promoters: [] }, isLoading: promotersLoading } =
        useGetPromotersQuery();

    const [form, setForm] = useState({
        id: fund.id,
        name: fund.name,
        fundRegulation: fund.fundRegulation,
        brochureAMF: fund.brochureAMF,
        advisorPresentation: fund.advisorPresentation,
        customerPresentation: fund.customerPresentation,
        cutoffDays: fund.cutoffDays,
        paymentDays: fund.paymentDays,
        image: fund.image,
        distributorArgument: fund.distributorArgument || '',
        webinarRegistrationUrl: fund.webinarRegistrationUrl || '',
        replayWebinarUrl: fund.replayWebinarUrl || '',
        regulatoryPack: fund.regulatoryPack || '',
        distributorImage: fund.distributorImage || '',
        sgp: fund?.sgp?.id || '',
        promoter: fund?.promoter?.id || '',
    });
    const [fileSizeErrors, setFileSizeErrors] = useState({
        fundRegulation: '',
        image: '',
        distributorImage: '',
        regulatoryPack: '',
        distributorArgument: '',
    });
    const [fileTypeErrors, setFileTypeErrors] = useState({
        fundRegulation: '',
        brochureAMF: '',
        advisorPresentation: '',
        customerPresentation: '',
        image: '',
        distributorImage: '',
        regulatoryPack: '',
        distributorArgument: '',
    });
    const [errors, setErrors] = useState({
        name: '',
        webinarRegistrationUrl: '',
        replayWebinarUrl: '',
        cutoffDays: '',
        paymentDays: '',
    });
    const [successfullyUpdated, setSuccessfullyUpdated] = useState(false);

    const schema = useMemo(
        () => ({
            name: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            cutoffDays: props.fund?.evergreen
                ? Joi.number().integer().min(0).max(30).required().messages({
                      'number.integer': texts.INVALID_INPUT,
                      'number.base': texts.SAISIR_LE_CHAMP,
                      'number.min': texts.INVALID_INPUT,
                      'number.max': texts.INVALID_INPUT,
                  })
                : Joi.any(),
            paymentDays: props.fund?.evergreen
                ? Joi.number().integer().min(0).required().messages({
                      'number.base': texts.SAISIR_LE_CHAMP,
                      'number.min': texts.INVALID_INPUT,
                  })
                : Joi.any(),
            webinarRegistrationUrl: Joi.string()
                .uri()
                .allow('')
                .messages({ 'string.uri': texts.URL_ERROR }),
        }),
        [props.fund]
    );

    const handleChange = useCallback(
        key => e => {
            setSuccessfullyUpdated(false);
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );

    const handleFileChange = useCallback(
        key => e => {
            const file = e.target.files[0];
            const fileExtension = getFileExtension(file);

            if (!validateFileSize(file)) {
                // set file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: texts.FILE_TOO_LARGE }));
                return;
            } else {
                // clear file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: '' }));
            }
            let hasFileTypeError = true;
            if (key === 'image' || key === 'distributorImage') {
                checkImageFile(file, () => {
                    hasFileTypeError = false;
                });
            } else if (
                (key === 'regulatoryPack' && fileExtension === 'zip') ||
                (['customerPresentation', 'advisorPresentation'].includes(key) &&
                    ['zip', 'pdf'].includes(fileExtension))
            ) {
                hasFileTypeError = false;
            } else if (fileExtension === 'pdf') {
                hasFileTypeError = false;
            }

            if (hasFileTypeError) {
                setFileTypeErrors(prevState => ({
                    ...prevState,
                    [key]: texts.FILE_TYPE_INCORRECT,
                }));
            } else {
                setFileTypeErrors(prevState => ({ ...prevState, [key]: '' }));
                setForm(prevState => ({ ...prevState, [key]: file }));
            }
        },
        []
    );
    const handleFileDelete = useCallback(
        key => () => {
            setFileTypeErrors(prevState => ({ ...prevState, [key]: '' }));
            setForm(prevState => ({ ...prevState, [key]: null }));
        },
        []
    );

    const handleUpdateFund = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        if (valid) {
            setErrors({ name: '', webinarRegistrationUrl: '', replayWebinarUrl: '' });
            updateInvestmentFund({
                ...form,
                id: fund.id,
                attributesUpdate: true,
                imageId: fund.image.id,
                fundRegulationId: fund.fundRegulation?.id,
                brochureAMFId: fund.brochureAMF?.id,
                advisorPresentationId: fund.advisorPresentation?.id,
                customerPresentationId: fund.customerPresentation?.id,
                distributorArgumentId: fund.distributorArgument?.id,
                regulatoryPackId: fund.regulatoryPack?.id,
                distributorImageId: fund.distributorImage?.id,
                promoter: form.promoter,
            });
        }
    }, [form, fund, updateInvestmentFund, schema]);

    useEffect(() => {
        if (isSuccess) {
            refetch();
            setSuccessfullyUpdated(true);
        }
    }, [isSuccess, refetch]);

    useEffect(() => {
        setForm({
            id: props.fund.id,
            name: props.fund.name,
            fundRegulation: props.fund.fundRegulation,
            brochureAMF: props.fund.brochureAMF,
            advisorPresentation: props.fund.advisorPresentation,
            customerPresentation: props.fund.customerPresentation,
            image: props.fund.image,
            distributorArgument: props.fund.distributorArgument || '',
            webinarRegistrationUrl: props.fund.webinarRegistrationUrl || '',
            replayWebinarUrl: props.fund.replayWebinarUrl || '',
            regulatoryPack: props.fund.regulatoryPack || '',
            distributorImage: props.fund.distributorImage || '',
            cutoffDays: props.fund.evergreen ? props.fund.cutoffDays || '' : undefined,
            paymentDays: props.fund.evergreen ? props.fund.paymentDays || '' : undefined,
            sgp: props.fund.sgp?.id || '',
            promoter: props.fund.promoter?.id || '',
        });
    }, [props.fund]);

    return (
        <ExpandableList title={texts.FUND_ATTRIBUTES}>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="flex-start"
                gap={2}
            >
                <Box sx={{ width: containerWidth }}>
                    <CustomTextField
                        label={texts.FUND_NAME}
                        value={form.name}
                        sx={{ width: '100%' }}
                        onChange={handleChange('name')}
                        error={errors.name}
                    />
                    {props.fund?.evergreen && (
                        <CustomTextField
                            type="number"
                            label={texts.CUT_OFF_DAYS}
                            value={form.cutoffDays}
                            sx={{ width: '100%' }}
                            onChange={handleChange('cutoffDays')}
                            error={errors.cutoffDays}
                        />
                    )}
                    {props.fund?.evergreen && (
                        <CustomTextField
                            type="number"
                            label={texts.PAYMENT_DAYS}
                            value={form.paymentDays}
                            sx={{ width: '100%' }}
                            onChange={handleChange('paymentDays')}
                            error={errors.paymentDays}
                        />
                    )}
                    <Loader loading={sgpsLoading} marginTop={2}>
                        <CustomSelect
                            fullWidth
                            label={texts.SGP_PROVIDER}
                            value={form.sgp}
                            onChange={handleChange('sgp')}
                        >
                            {sgps.map(renderSGP)}
                        </CustomSelect>
                    </Loader>
                    <Loader loading={promotersLoading}>
                        <CustomSelect
                            fullWidth
                            label={texts.PROMOTER}
                            value={form.promoter}
                            onChange={handleChange('promoter')}
                        >
                            {promoters.map(promoter => (
                                <MenuItem key={promoter.id} value={promoter.id}>
                                    {promoter.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Loader>
                </Box>
                <FundFile
                    id="fund-regulation"
                    onChange={handleFileChange('fundRegulation')}
                    name={form.fundRegulation?.name || texts.FUND_REGULATIONS}
                    label={texts.FUND_REGULATIONS}
                    typeError={!!fileTypeErrors.fundRegulation}
                    sizeError={!!fileSizeErrors.fundRegulation}
                    file={form.fundRegulation}
                />
                <FundFile
                    id="fund-logo"
                    onChange={handleFileChange('image')}
                    name={form.image?.name || texts.LOGO}
                    label={texts.LOGO}
                    typeError={!!fileTypeErrors.image}
                    sizeError={!!fileSizeErrors.image}
                    file={form.image}
                />
                <FundFile
                    id="dist-arg"
                    onChange={handleFileChange('distributorArgument')}
                    onDelete={handleFileDelete('distributorArgument')}
                    name={form.distributorArgument?.name || texts.CONSULTANT_ARGUMENT}
                    label={texts.CONSULTANT_ARGUMENT}
                    typeError={!!fileTypeErrors.distributorArgument}
                    sizeError={!!fileSizeErrors.distributorArgument}
                    file={form.distributorArgument}
                />
                <FundFile
                    id="reg-pack"
                    acceptedFileTypes=".zip"
                    onChange={handleFileChange('regulatoryPack')}
                    onDelete={handleFileDelete('regulatoryPack')}
                    name={form.regulatoryPack?.name || texts.REGULATORY_PACK}
                    label={texts.REGULATORY_PACK}
                    typeError={!!fileTypeErrors.regulatoryPack}
                    sizeError={!!fileSizeErrors.regulatoryPack}
                    file={form.regulatoryPack}
                />
                <FundFile
                    id="adv-pres"
                    acceptedFileTypes=".pdf, .zip"
                    onChange={handleFileChange('advisorPresentation')}
                    onDelete={handleFileDelete('advisorPresentation')}
                    name={form.advisorPresentation?.name || texts.ADVISOR_PRESENTATION}
                    label={texts.ADVISOR_PRESENTATION}
                    typeError={!!fileTypeErrors.advisorPresentation}
                    sizeError={!!fileSizeErrors.advisorPresentation}
                    file={form.advisorPresentation}
                />
                <FundFile
                    id="broc-amf"
                    acceptedFileTypes=".pdf"
                    onChange={handleFileChange('brochureAMF')}
                    onDelete={handleFileDelete('brochureAMF')}
                    name={form.brochureAMF?.name || texts.BROCHURE_AMF}
                    label={texts.BROCHURE_AMF}
                    typeError={!!fileTypeErrors.brochureAMF}
                    sizeError={!!fileSizeErrors.brochureAMF}
                    file={form.brochureAMF}
                />
                <FundFile
                    id="cust-pres"
                    acceptedFileTypes=".pdf, .zip"
                    onChange={handleFileChange('customerPresentation')}
                    onDelete={handleFileDelete('customerPresentation')}
                    name={form.customerPresentation?.name || texts.CUSTOMER_PRESENTATION}
                    label={texts.CUSTOMER_PRESENTATION}
                    typeError={!!fileTypeErrors.customerPresentation}
                    sizeError={!!fileSizeErrors.customerPresentation}
                    file={form.customerPresentation}
                />
                <Box sx={{ width: containerWidth }}>
                    <CustomTextField
                        label={texts.INSCRIPTION_WEBINAR}
                        value={form.webinarRegistrationUrl}
                        sx={{ width: '100%' }}
                        onChange={handleChange('webinarRegistrationUrl')}
                        error={errors.webinarRegistrationUrl}
                    />
                </Box>
                <Box sx={{ width: containerWidth }}>
                    <CustomTextField
                        label={texts.REPLAY_WEBINAR}
                        value={form.replayWebinarUrl}
                        sx={{ width: '100%' }}
                        onChange={handleChange('replayWebinarUrl')}
                        error={errors.replayWebinarUrl}
                    />
                </Box>
                <FundFile
                    id="partner-logo"
                    onChange={handleFileChange('distributorImage')}
                    onDelete={handleFileDelete('distributorImage')}
                    name={form.distributorImage?.name || texts.PARTNER_LOGO}
                    label={texts.PARTNER_LOGO}
                    typeError={!!fileTypeErrors.distributorImage}
                    sizeError={!!fileSizeErrors.distributorImage}
                    file={form.distributorImage}
                />
                <ActionButton
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleUpdateFund}
                    variant="contained"
                >
                    {texts.SAVE}
                </ActionButton>
                {successfullyUpdated && <SuccessMessage message={texts.INFO_SAVED} />}
            </Box>
        </ExpandableList>
    );
};

export default FundAttributes;
